/** Choosing which column of a customer's file is which of ours (Gate 38).
 *
 * Pure, and kept apart from the screen so the one rule that matters can be tested without
 * rendering anything: **the comparison is not offered while a required column has nothing matched
 * to it, or while two columns of the file are claiming the same one.** The server refuses both
 * too; this is the same rule stated early enough that the person is told *which* column, and is
 * never shown a button that predictably fails (design rule 6).
 *
 * Nothing here guesses. `initialChoice` only copies what the server *proposed*, and a proposal
 * the server withheld (two headers could each be the quantity) stays empty until a person fills
 * it.
 */

import type { ColumnMap, ColumnsNeedMapping } from "../api/queries";

/** A choice per header: one of our column names, or `""` for *do not import this column*. */
export type Choice = Readonly<Record<string, string>>;

export const NOT_IMPORTED = "";

/** Each of our columns in the words a customer reads it in. The machine name stays beside it
 * because it is also the header of the template they may have downloaded. */
export const TARGET_LABEL: Readonly<Record<string, string>> = {
  facility: "Site",
  category: "Category",
  quantity: "Quantity",
  unit: "Unit",
  period_start: "Period start",
  period_end: "Period end",
  fuel: "Fuel",
  meter: "Meter reference",
  region: "Grid region",
  notes: "Notes",
  line_id: "Line id (round trip)",
  revision_id: "Revision id (round trip)",
};

/** What the server proposed, and nothing it did not. */
export function initialChoice(needs: ColumnsNeedMapping): Choice {
  const choice: Record<string, string> = {};
  for (const column of needs.columns) choice[column.header] = column.suggestion ?? NOT_IMPORTED;
  return choice;
}

/** Every reason the comparison cannot be run yet, each naming the column. Empty means it can. */
export function problems(needs: ColumnsNeedMapping, choice: Choice): string[] {
  const found: string[] = [];
  const claimed = new Map<string, string[]>();
  for (const column of needs.columns) {
    const target = choice[column.header] ?? NOT_IMPORTED;
    if (target === NOT_IMPORTED) continue;
    claimed.set(target, [...(claimed.get(target) ?? []), column.header]);
  }
  for (const target of needs.targets) {
    if (target.required && !claimed.has(target.name)) {
      found.push(
        `The required column “${target.name}” (${TARGET_LABEL[target.name] ?? target.name}) has no column of your file matched to it.`,
      );
    }
  }
  for (const [target, headers] of claimed) {
    if (headers.length > 1) {
      found.push(
        `${headers.map((header) => `“${header}”`).join(" and ")} are both set to “${target}”. A column of your file can be only one of ours — choose which.`,
      );
    }
  }
  return found;
}

/** The choices a saved mapping would fill in - or `null` when it does not cover this file.
 *
 * ADR-0104. **Fully, or not at all.** The server only offers a mapping for exactly these columns,
 * and this is the same rule stated again at the point of use: every column of the file must have an
 * entry, and every entry must be one of our columns (or `null`). A mapping that is short, or names
 * a target the screen does not offer, is not partly applied - the person is asked, as before.
 *
 * It returns choices for the screen to *show*. Nothing is sent from here: the comparison still
 * needs the person to press the button.
 */
export function choiceFromSaved(needs: ColumnsNeedMapping, saved: ColumnMap): Choice | null {
  const known = new Set(needs.targets.map((target) => target.name));
  const choice: Record<string, string> = {};
  for (const column of needs.columns) {
    // A column with no heading cannot have been remembered; it keeps the server's proposal.
    if (column.header.trim() === "") {
      choice[column.header] = column.suggestion ?? NOT_IMPORTED;
      continue;
    }
    if (!(column.header in saved)) return null;
    const target = saved[column.header];
    if (target === null || target === undefined || target === "") {
      choice[column.header] = NOT_IMPORTED;
    } else if (known.has(target)) {
      choice[column.header] = target;
    } else {
      return null;
    }
  }
  return choice;
}

/** The mapping to send. **Every** column of the file is listed, the ones left out as `null`, so
 * the server is told what was decided about each rather than inferring it from what is missing. */
export function toColumnMap(needs: ColumnsNeedMapping, choice: Choice): ColumnMap {
  const map: ColumnMap = {};
  for (const column of needs.columns) {
    const target = choice[column.header] ?? NOT_IMPORTED;
    map[column.header] = target === NOT_IMPORTED ? null : target;
  }
  return map;
}
